import { getCallParams, makeCall } from "utils/service";
import urls from "global/constants/UrlConstants";
import {
  AiDesign,
  AiMessageResponse,
  AiPublishRequest,
  AiPublishResponse,
  AiSessionDetail,
  AiSessionList,
  AiStartResponse,
  AiTemplateSummary,
  AiVersionList,
} from "./AiWebFormTypesAndValidation";

/**
 * The AI designer is reached through core only; core passes the AI's status,
 * body and `retryAfter` on unchanged, so callers branch on `statusCode`.
 *
 * Starting reads the whole document and can take a minute, and a chat turn up
 * to 25 s on the AI side; core waits 75 s for either, so the browser waits a
 * little longer than core instead of `makeCall`'s 30 s default.
 */
const START_TIMEOUT_MS = 90_000;
const TURN_TIMEOUT_MS = 80_000;

const query = (params: Record<string, string | number | null | undefined>) => {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== "") {
      search.append(key, String(value));
    }
  });
  const text = search.toString();
  return text ? `?${text}` : "";
};

export const startAiSession = async (body: {
  templateId: string;
  signerId: number;
  prompt: string | null;
}): Promise<AiStartResponse> => {
  const callParams = await getCallParams("POST", body);
  return await makeCall(`${urls.AI_WEBFORM_ADD}/sessions`, callParams, true, {
    timeoutMs: START_TIMEOUT_MS,
  });
};

export const listAiSessions = async (params: {
  templateId?: string;
  signerId?: number;
  limit?: number;
  cursor?: string | null;
}): Promise<AiSessionList> => {
  const callParams = await getCallParams("GET");
  return await makeCall(
    `${urls.AI_WEBFORM_FETCH}/sessions${query(params)}`,
    callParams,
  );
};

export const getAiSession = async (
  sessionId: string,
  params: { limit?: number; before?: number | null } = {},
): Promise<AiSessionDetail> => {
  const callParams = await getCallParams("GET");
  return await makeCall(
    `${urls.AI_WEBFORM_FETCH}/sessions/${encodeURIComponent(sessionId)}${query(params)}`,
    callParams,
  );
};

export const sendAiMessage = async (
  sessionId: string,
  body: { message: string; baseVersion: number | null },
): Promise<AiMessageResponse> => {
  const callParams = await getCallParams("POST", body);
  return await makeCall(
    `${urls.AI_WEBFORM_UPDATE}/sessions/${encodeURIComponent(sessionId)}/messages`,
    callParams,
    true,
    { timeoutMs: TURN_TIMEOUT_MS },
  );
};

export const hideAiSession = async (
  sessionId: string,
): Promise<{ sessionId: string; hidden: boolean }> => {
  const callParams = await getCallParams("POST");
  return await makeCall(
    `${urls.AI_WEBFORM_UPDATE}/sessions/${encodeURIComponent(sessionId)}/hide`,
    callParams,
  );
};

export const getAiDesign = async (
  designId: string,
  version?: number | null,
): Promise<AiDesign> => {
  const callParams = await getCallParams("GET");
  return await makeCall(
    `${urls.AI_WEBFORM_FETCH}/designs/${encodeURIComponent(designId)}${query({ version })}`,
    callParams,
  );
};

export const getAiVersions = async (designId: string): Promise<AiVersionList> => {
  const callParams = await getCallParams("GET");
  return await makeCall(
    `${urls.AI_WEBFORM_FETCH}/designs/${encodeURIComponent(designId)}/versions`,
    callParams,
  );
};

export const restoreAiVersion = async (
  designId: string,
  version: number,
): Promise<AiDesign> => {
  const callParams = await getCallParams("POST", { version });
  return await makeCall(
    `${urls.AI_WEBFORM_UPDATE}/designs/${encodeURIComponent(designId)}/restore`,
    callParams,
  );
};

export const publishAiDesign = async (
  designId: string,
  body: AiPublishRequest,
): Promise<AiPublishResponse> => {
  const callParams = await getCallParams("POST", body);
  return await makeCall(
    `${urls.AI_WEBFORM_UPDATE}/designs/${encodeURIComponent(designId)}/publish`,
    callParams,
    true,
    { timeoutMs: TURN_TIMEOUT_MS },
  );
};

/** The template signer behind a design; `fieldsMatch` only when a design is named. */
export const getAiTemplateSummary = async (params: {
  templateId: string;
  signerId: number;
  designId?: string | null;
}): Promise<AiTemplateSummary> => {
  const callParams = await getCallParams("GET");
  return await makeCall(`${urls.AI_WEBFORM_FETCH}/template${query(params)}`, callParams);
};
